import { Component, model, input, inject, ViewChild, effect, ChangeDetectorRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { NgxScannerQrcodeComponent, ScannerQRCodeResult } from 'ngx-scanner-qrcode';

import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-qrscanner-control',
  standalone: true,
  imports: [CommonModule, DialogModule, TagModule, NgxScannerQrcodeComponent],
  template: `
    <div class="checklist-control-container" [class.checklist-control-container--error]="hasError()">
      <div class="ctrl-container-hdr">
        <div class="qr-label-group">
          <label class="checklist-control-label">
            {{ lineItem().name }}
            @if (lineItem().isRequired) {
              <span class="required-indicator">*</span>
            }
          </label>
          @if (scannedCodes.length > 0) {
            <span class="qr-count">({{ scannedCodes.length }} Codes)</span>
          }
        </div>
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>

      <button type="button" class="ctrl-photo-add" (click)="openScanner()" [disabled]="disabled()">
        <i class="ri-qr-code-line"></i>Scan QR Code
      </button>

      <!-- Scanned QR Codes List -->
      @if (scannedCodes.length > 0) {
        <div class="qr-code-list">
          <div class="qr-code-list-label">Scanned</div>
          @for (code of scannedCodes; track $index) {
            <div class="qr-code-item">
              <div class="qr-code-info">
                <i class="ri-qr-scan-2-line qr-code-icon"></i>
                <span class="qr-code-text">{{ code }}</span>
              </div>
              @if (!disabled()) {
                <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger"
                  (click)="removeCode($index)" title="Remove">
                  <i class="ri-close-line"></i>
                </button>
              }
            </div>
          }
        </div>
      }

      @if (scannedCodes.length === 0) {
        <div class="qr-empty">No QR codes scanned</div>
      }
      @if (hasError()) {
        @for (err of errors(); track $index) {
          <small class="p-error d-block">{{ err }}</small>
        }
      }
    </div>

    <!-- QR Scanner Dialog -->
    <p-dialog
      [(visible)]="showScanner"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      header="Scan QR Code"
      [style]="{ width: '500px' }"
      (onHide)="stopScanner()"
    >
      <div class="scanner-container">
        <div class="camera-controls">
          <button type="button" class="ctrl-action-btn" (click)="switchCamera()"
            [title]="'Switch to ' + (currentCamera === 'environment' ? 'front' : 'back') + ' camera'">
            <i class="ri-camera-switch-line"></i>
            {{ currentCamera === 'environment' ? 'Back Camera' : 'Front Camera' }}
          </button>
        </div>

        @if (showCamera) {
          <ngx-scanner-qrcode
            #scanner
            [config]="config"
            (event)="onScanSuccess($event)"
          ></ngx-scanner-qrcode>
        }

        @if (lastScannedCode) {
          <div class="scan-result">
            <i class="ri-checkbox-circle-line scan-result-icon"></i>
            <span class="scan-result-text">{{ lastScannedCode }}</span>
          </div>
        }
      </div>
    </p-dialog>
  `,
  styles: [`
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 14px 16px;
      background: var(--color-white);
    }
    .checklist-control-container--error {
      border: 1px solid #ef4444;
      border-radius: 6px;
      background: #fef2f2;
    }
    .qr-label-group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .qr-count {
      font-size: 12px;
      color: var(--color-grey-500);
    }
    .qr-code-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .qr-code-list-label {
      font-size: 12px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-grey-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .qr-code-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border: 1px solid var(--color-border-default);
      border-radius: 6px;
      background: var(--color-white);
    }
    .qr-code-info {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }
    .qr-code-icon {
      font-size: 20px;
      color: var(--color-success-mid);
      flex-shrink: 0;
    }
    .qr-code-text {
      font-size: 14px;
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .qr-empty {
      font-size: 14px;
      color: var(--color-grey-500);
    }
    .scanner-container {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: center;
    }
    .camera-controls {
      display: flex;
      justify-content: center;
      width: 100%;
    }
    .scan-result {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      background: var(--color-success-low);
      border: 1px solid var(--color-success-mid);
      border-radius: 6px;
      width: 100%;
    }
    .scan-result-icon {
      font-size: 20px;
      color: var(--color-success-mid);
      flex-shrink: 0;
    }
    .scan-result-text {
      font-size: 14px;
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }
    ::ng-deep ngx-scanner-qrcode video {
      width: 100%;
      max-width: 450px;
      border-radius: 6px;
    }
  `]
})
export class QrscannerControlComponent {
  @ViewChild('scanner') scanner!: NgxScannerQrcodeComponent;

  lineItem = input.required<LineItemModel>();
  value = model<any>('[]');
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  scannedCodes: string[] = [];
  showScanner: boolean = false;
  showCamera: boolean = false;
  lastScannedCode: string = '';
  currentCamera: 'environment' | 'user' = 'environment';
  private isProcessingScan = false;

  private cdr = inject(ChangeDetectorRef);
  private speechService = inject(SpeechService);
  private validationService = inject(ValidationService);

  config = {
    constraints: {
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: 'environment'
      }
    },
    isBeep: true
  };

  constructor() {
    // Watch for value changes and sync to scannedCodes array
    effect(() => {
      const currentValue = this.value();
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        this.scannedCodes = [];
        return;
      }

      // Offline sync paths can provide an already-parsed array.
      if (Array.isArray(currentValue)) {
        this.scannedCodes = currentValue.map((code) => String(code)).filter((code) => code.length > 0);
        return;
      }

      if (typeof currentValue === 'string') {
        try {
          const parsedCodes = JSON.parse(currentValue);
          if (Array.isArray(parsedCodes)) {
            this.scannedCodes = parsedCodes.map((code) => String(code)).filter((code) => code.length > 0);
            return;
          }
        } catch {
          // If it's not JSON, treat as a single scanned code string.
          this.scannedCodes = [currentValue];
          return;
        }
      }

      this.scannedCodes = [];
    });
  }

  errors = computed(() => {
    if (!this.showError()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  openScanner(): void {
    this.isProcessingScan = false;
    this.showScanner = true;
    this.showCamera = false;
    this.lastScannedCode = '';
    this.currentCamera = 'environment'; // Always start with back camera
    this.config.constraints.video.facingMode = 'environment';

    // Start scanner after dialog opens
    setTimeout(() => {
      this.showCamera = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        if (this.scanner) {
          this.scanner.start();
        }
      }, 100);
    }, 300);
  }

  stopScanner(): void {
    if (this.scanner) {
      this.scanner.stop();

      // Manually stop all media tracks to ensure camera is fully released
      const videoElement = this.scanner.video?.nativeElement;
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
        });
        videoElement.srcObject = null;
      }
    }
    this.showCamera = false;
    this.showScanner = false;
    this.isProcessingScan = false;
    this.cdr.markForCheck();
  }

  onScanSuccess(event: ScannerQRCodeResult[]): void {
    if (this.isProcessingScan) return;

    if (event && event.length > 0) {
      const qrCode = event[0].value;

      if (!this.scannedCodes.includes(qrCode)) {
        this.isProcessingScan = true;

        // Must call stop() before removing from DOM — library's ngOnDestroy only calls pause()
        // pause() does not stop MediaStream tracks, so camera hardware stays on without this
        if (this.scanner) {
          this.scanner.stop();
        }

        // Remove scanner from DOM
        this.showCamera = false;

        this.scannedCodes.push(qrCode);
        this.lastScannedCode = qrCode;
        this.value.set(JSON.stringify(this.scannedCodes));
        this.cdr.markForCheck();

        // Close dialog after briefly showing the result
        setTimeout(() => {
          this.showScanner = false;
          this.isProcessingScan = false;
          this.cdr.markForCheck();
        }, 1000);
      }
    }
  }

  removeCode(index: number): void {
    this.scannedCodes.splice(index, 1);
    this.value.set(JSON.stringify(this.scannedCodes));
  }

  switchCamera(): void {
    // Toggle between front and back camera
    this.currentCamera = this.currentCamera === 'environment' ? 'user' : 'environment';

    // Update config
    this.config.constraints.video.facingMode = this.currentCamera;

    // Stop current scanner
    if (this.scanner) {
      this.scanner.stop();
      const videoElement = this.scanner.video?.nativeElement;
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoElement.srcObject = null;
      }
    }

    // Restart scanner with new camera
    setTimeout(() => {
      if (this.scanner) {
        this.scanner.start();
      }
      this.cdr.markForCheck();
    }, 300);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
